
import React, { useState } from 'react';
import Button from '../../../components/base/Button';

const TreatmentsSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);

  const treatments = [
    {
      category: 'Hormone Therapy',
      icon: 'ri-heart-pulse-line',
      treatments: [
        {
          name: 'Testosterone Replacement Therapy',
          description: 'Restore optimal testosterone levels for improved energy, muscle mass, and overall vitality.',
          benefits: ['Increased Energy', 'Improved Mood', 'Enhanced Libido', 'Better Sleep'],
          duration: '3-6 months',
          price: 'Starting at $299/month'
        },
        {
          name: 'Estrogen Optimization',
          description: 'Balance estrogen levels to alleviate symptoms and improve quality of life.',
          benefits: ['Reduced Hot Flashes', 'Better Mood', 'Improved Bone Health', 'Enhanced Skin'],
          duration: '2-4 months',
          price: 'Starting at $249/month'
        },
        {
          name: 'Thyroid Support',
          description: 'Optimize thyroid function for better metabolism and energy levels.',
          benefits: ['Weight Management', 'Increased Energy', 'Better Focus', 'Improved Metabolism'],
          duration: '2-3 months',
          price: 'Starting at $199/month'
        }
      ]
    },
    {
      category: 'Peptide Therapy',
      icon: 'ri-dna-line',
      treatments: [
        {
          name: 'Growth Hormone Peptides',
          description: 'Stimulate natural growth hormone production for anti-aging benefits.',
          benefits: ['Muscle Growth', 'Fat Loss', 'Better Recovery', 'Improved Sleep'],
          duration: '3-6 months',
          price: 'Starting at $399/month'
        },
        {
          name: 'Cognitive Enhancement Peptides',
          description: 'Improve brain function, memory, and mental clarity.',
          benefits: ['Enhanced Memory', 'Better Focus', 'Improved Learning', 'Mental Clarity'],
          duration: '2-4 months',
          price: 'Starting at $349/month'
        },
        {
          name: 'Recovery Peptides',
          description: 'Accelerate healing and recovery from injuries or intense training.',
          benefits: ['Faster Healing', 'Reduced Inflammation', 'Better Recovery', 'Pain Relief'],
          duration: '1-3 months',
          price: 'Starting at $279/month'
        }
      ]
    },
    {
      category: 'IV Therapy',
      icon: 'ri-drop-line',
      treatments: [
        {
          name: 'NAD+ Infusion',
          description: 'Boost cellular energy and promote longevity with NAD+ therapy.',
          benefits: ['Cellular Repair', 'Energy Boost', 'Mental Clarity', 'Anti-Aging'],
          duration: '1-2 hours per session',
          price: 'Starting at $499/session'
        },
        {
          name: 'Glutathione Therapy',
          description: 'Powerful antioxidant therapy for detoxification and immune support.',
          benefits: ['Detoxification', 'Immune Support', 'Skin Health', 'Energy Enhancement'],
          duration: '30-60 minutes',
          price: 'Starting at $199/session'
        },
        {
          name: 'Vitamin Cocktails',
          description: 'Customized vitamin blends for optimal nutrient absorption.',
          benefits: ['Nutrient Boost', 'Immune Support', 'Energy Enhancement', 'Hydration'],
          duration: '30-45 minutes',
          price: 'Starting at $149/session'
        }
      ]
    }
  ];

  return (
    <section id="treatments" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Comprehensive <span className="text-blue-600">Treatment Options</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore our full range of innovative treatments designed to optimize your health, 
            enhance performance, and promote longevity through advanced <strong>telemedicine</strong> care.
          </p>
        </div>

        <div className="mb-12">
          <div className="flex flex-wrap justify-center gap-4">
            {treatments.map((category, index) => (
              <button
                key={index}
                onClick={() => setActiveTab(index)}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeTab === index
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                <i className={`${category.icon} mr-2`}></i>
                {category.category}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {treatments[activeTab].treatments.map((treatment, index) => (
            <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{treatment.name}</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">{treatment.description}</p>
              
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Key Benefits:</h4>
                <div className="grid grid-cols-2 gap-2">
                  {treatment.benefits.map((benefit, benefitIndex) => (
                    <div key={benefitIndex} className="flex items-center">
                      <i className="ri-check-line text-teal-600 mr-2"></i>
                      <span className="text-sm text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="border-t border-gray-200 pt-6">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <p className="text-sm text-gray-500">Duration</p>
                    <p className="font-semibold text-gray-900">{treatment.duration}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Price</p>
                    <p className="font-semibold text-blue-600">{treatment.price}</p>
                  </div>
                </div>
                
                <Button variant="primary" className="w-full">
                  Learn More
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-16">
          <div className="bg-blue-600 rounded-2xl p-8 text-white">
            <h3 className="text-3xl font-bold mb-4">Ready to Start Your Journey?</h3>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Schedule a consultation with our medical experts to create a personalized treatment plan 
              tailored to your unique health goals and needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="secondary" size="lg">
                <i className="ri-calendar-line mr-2"></i>
                Schedule Consultation
              </Button>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-600">
                <i className="ri-phone-line mr-2"></i>
                Call Now: (555) VITAL-MED
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TreatmentsSection;
